/**
 * Consent Component (Dual-Mode Support)
 *
 * First step in the intro flow - displays data consent form to participants.
 * Players must consent before proceeding to instructions and the game.
 *
 * Mode-aware:
 * - DYAD mode: Mentions partner pairing and chat interaction
 * - SINGLE mode: Describes self-directed learning (no partner)
 *
 * Intro Flow Order:
 * 1. Consent (this component)
 * 2. InstructionStepOne
 * 3. DomainRanking
 * 4. Game starts
 */

import React, { useState, useEffect, useRef } from "react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";

// Admin API base: direct port in local dev, Caddy proxy path in production
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const ADMIN_API_BASE = isLocalhost ? "http://localhost:5002" : "";

export function Consent({ next }) {
  const game = useGame();
  const player = usePlayer();
  const treatment = game?.get("treatment") || {};
  const gameMode = treatment.gameMode || "dyad";
  const isSingleMode = gameMode === "single";

  const [consents, setConsents] = useState({
    readUnderstood: false,
    voluntary: false,
    confidential: false,
    openData: false,
    recorded: false,
    ageConfirm: false,
  });

  // Turnstile anti-bot gate. States: "loading" (fetching config),
  // "off" (disabled/bypassed), "pending" (widget shown, not solved),
  // "verified", "failed" (server rejected the token).
  const [botCheck, setBotCheck] = useState("loading");
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);

  const allConsented = Object.values(consents).every(Boolean);
  const consentCount = Object.values(consents).filter(Boolean).length;
  const totalConsents = Object.keys(consents).length;
  const botCheckPassed = botCheck === "off" || botCheck === "verified";

  useEffect(() => {
    console.log(`🔵 CONSENT FORM LOADED - Mode: ${gameMode}`);
  }, [gameMode]);

  // Fetch Turnstile config; if enabled, load the Cloudflare script and render
  // the widget. Fail-open on any config/network problem (a broken sidecar must
  // never lock real participants out of the study).
  useEffect(() => {
    let cancelled = false;

    const recordBypass = (reason) => {
      player?.set("botCheck", {
        provider: "turnstile",
        verified: false,
        bypassed: reason,
        timestamp: new Date().toISOString(),
      });
    };

    const verifyToken = async (token) => {
      try {
        const res = await fetch(`${ADMIN_API_BASE}/api/turnstile-verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.success) {
          setBotCheck("verified");
          player?.set("botCheck", {
            provider: "turnstile",
            verified: true,
            bypassed: data.bypassed || null,
            timestamp: new Date().toISOString(),
          });
        } else {
          setBotCheck("failed");
          player?.set("botCheck", {
            provider: "turnstile",
            verified: false,
            rejected: true,
            codes: data.codes || [],
            timestamp: new Date().toISOString(),
          });
        }
      } catch (err) {
        console.warn("[Turnstile] Verify request failed, failing open:", err);
        if (!cancelled) {
          setBotCheck("off");
          recordBypass("verify-unreachable");
        }
      }
    };

    const renderWidget = (siteKey) => {
      if (cancelled || !window.turnstile || !widgetRef.current) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: siteKey,
        theme: "light",
        callback: (token) => verifyToken(token),
        "error-callback": () => {
          // Widget-level error (network, unsupported browser) — let them retry
          if (!cancelled) setBotCheck("pending");
        },
      });
    };

    (async () => {
      try {
        const res = await fetch(`${ADMIN_API_BASE}/api/turnstile-config`);
        const cfg = await res.json();
        if (cancelled) return;
        if (!cfg.enabled) {
          setBotCheck("off");
          recordBypass("disabled");
          return;
        }
        setBotCheck("pending");
        if (window.turnstile) {
          renderWidget(cfg.siteKey);
        } else {
          const cbName = "__turnstileOnload";
          window[cbName] = () => renderWidget(cfg.siteKey);
          const script = document.createElement("script");
          script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${cbName}`;
          script.async = true;
          document.head.appendChild(script);
        }
      } catch (err) {
        console.warn("[Turnstile] Config fetch failed, failing open:", err);
        if (!cancelled) {
          setBotCheck("off");
          recordBypass("config-unreachable");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* already gone */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryBotCheck = () => {
    if (window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
      setBotCheck("pending");
    }
  };

  // Human-interaction telemetry (anti-automation, always on).
  // Scripted browsers click by teleporting the cursor: near-zero mousemove
  // events, no organic path, no scrolling. Real humans produce hundreds of
  // moves just reading this screen. Recorded once at consent submit as
  // analysis-time flags — never blocks anyone (touch devices legitimately
  // produce zero mouse movement, so this must stay a flag, not a gate).
  const telemetryRef = useRef({
    startedAt: Date.now(),
    mouseMoves: 0,
    mouseDistance: 0,
    gridCells: new Set(),
    clicks: 0,
    keys: 0,
    scrolls: 0,
    touches: 0,
    lastX: null,
    lastY: null,
  });

  useEffect(() => {
    const t = telemetryRef.current;
    const onMove = (e) => {
      t.mouseMoves++;
      if (t.lastX !== null) {
        t.mouseDistance += Math.hypot(e.clientX - t.lastX, e.clientY - t.lastY);
      }
      t.lastX = e.clientX;
      t.lastY = e.clientY;
      // 40px grid — distinct cells visited approximates path coverage
      t.gridCells.add(`${Math.floor(e.clientX / 40)},${Math.floor(e.clientY / 40)}`);
    };
    const onClick = () => t.clicks++;
    const onKey = () => t.keys++;
    const onScroll = () => t.scrolls++;
    const onTouch = () => t.touches++;
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", onClick, { passive: true });
    window.addEventListener("keydown", onKey, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("touchstart", onTouch, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchstart", onTouch);
    };
  }, []);

  const buildTelemetry = () => {
    const t = telemetryRef.current;
    return {
      screen: "consent",
      durationMs: Date.now() - t.startedAt,
      mouseMoves: t.mouseMoves,
      mouseDistancePx: Math.round(t.mouseDistance),
      distinctGridCells: t.gridCells.size,
      clicks: t.clicks,
      keys: t.keys,
      scrolls: t.scrolls,
      touches: t.touches,
      // Automation fingerprint signals
      webdriver: navigator.webdriver === true,
      pluginCount: navigator.plugins?.length ?? null,
      languages: navigator.languages?.length ?? null,
      hasTouchSupport: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      screenSize: `${window.screen?.width}x${window.screen?.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
    };
  };

  const handleConsent = () => {
    console.log("✅ CONSENT BUTTON CLICKED - Calling next()");
    // Persist the consent record: which items were confirmed and when.
    // Required for the ethics audit trail (age confirmation, open-data sharing).
    player?.set("consent", {
      ...consents,
      allConsented,
      gameMode,
      timestamp: new Date().toISOString(),
    });
    // Anti-automation telemetry snapshot (see comment above buildTelemetry)
    player?.set("humanTelemetry", buildTelemetry());
    next();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-8 py-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <ShieldCheckIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Research Consent</h1>
                <p className="text-blue-100 text-sm mt-1">University of Cambridge • Trace Lab</p>
              </div>
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">
              Please review the study information below before participating in this research.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 divide-x divide-gray-100 bg-gray-50 border-b border-gray-100">
            <QuickStat
              icon={<ClockIcon />}
              label="Duration"
              value={isSingleMode ? "15-20 min" : "30-35 min"}
            />
            <QuickStat
              icon={<ShieldIcon />}
              label="Privacy"
              value="Anonymous"
            />
            <QuickStat
              icon={<HeartIcon />}
              label="Risk Level"
              value="Minimal"
            />
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Study Overview Section */}
            <Section title="Study Overview" icon={<BookIcon />}>
              <p className="text-gray-600 leading-relaxed">
                Thank you for your interest in this research study.
                {isSingleMode
                  ? " You will learn about an educational topic using provided materials, then complete a comprehension quiz."
                  : " You will be randomly assigned to learn information under specific conditions, then teach it to another participant or learn from them. Both participants will complete a comprehension quiz afterward."}
              </p>
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-sm text-blue-800">
                  <strong>Topics:</strong> You will learn about neutral, everyday subjects—like the history of a household invention,
                  the rules of an unfamiliar game, or how a common process works.
                </p>
              </div>
            </Section>

            {/* What to Expect */}
            <Section title="What to Expect" icon={<ListIcon />}>
              <div className="space-y-3">
                <ExpectationItem
                  number="1"
                  title="Duration"
                  description={
                    isSingleMode
                      ? "The study takes approximately 15–20 minutes to complete."
                      : "The study takes approximately 30–35 minutes, including a 10-minute live chat session."
                  }
                />
                {!isSingleMode && (
                  <ExpectationItem
                    number="2"
                    title="Your Partner"
                    description="You will be paired with another Prolific participant selected at random from those taking part at the same time."
                  />
                )}
                <ExpectationItem
                  number={isSingleMode ? "2" : "3"}
                  title="Data Collection"
                  description={
                    isSingleMode
                      ? "We collect your survey and quiz responses, along with your educational level and age."
                      : "We collect all written exchanges with your partner, survey and quiz responses, plus your educational level and age."
                  }
                />
              </div>
            </Section>

            {/* Your Rights */}
            <Section title="Your Rights" icon={<UserIcon />}>
              <div className="grid sm:grid-cols-3 gap-3">
                <RightCard
                  icon={<LockIcon />}
                  title="Data Protected"
                  description="All information is anonymized and secured"
                />
                <RightCard
                  icon={<ArchiveIcon />}
                  title="Stored Securely"
                  description="Data kept for minimum 10 years per policy"
                />
                <RightCard
                  icon={<MailIcon />}
                  title="Contact Us"
                  description="trace@chia.cam.ac.uk for any questions"
                />
              </div>
            </Section>

            {/* Consent Checkboxes */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-gray-900">Consent Confirmation</h3>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {consentCount} of {totalConsents}
                </span>
              </div>

              <div className="space-y-3">
                <ConsentCheckbox
                  checked={consents.readUnderstood}
                  onCheckedChange={(checked) =>
                    setConsents((s) => ({ ...s, readUnderstood: checked }))
                  }
                  label="I have read and understood the study information"
                />
                <ConsentCheckbox
                  checked={consents.voluntary}
                  onCheckedChange={(checked) =>
                    setConsents((s) => ({ ...s, voluntary: checked }))
                  }
                  label="I understand my participation is voluntary and I can withdraw anytime"
                />
                <ConsentCheckbox
                  checked={consents.confidential}
                  onCheckedChange={(checked) =>
                    setConsents((s) => ({ ...s, confidential: checked }))
                  }
                  label="I understand my personal information will remain confidential"
                />
                <ConsentCheckbox
                  checked={consents.openData}
                  onCheckedChange={(checked) =>
                    setConsents((s) => ({ ...s, openData: checked }))
                  }
                  label="I agree that anonymized data may be shared with the research community"
                />
                <ConsentCheckbox
                  checked={consents.recorded}
                  onCheckedChange={(checked) =>
                    setConsents((s) => ({ ...s, recorded: checked }))
                  }
                  label="I agree that my participation will be recorded for research purposes only"
                />
                <ConsentCheckbox
                  checked={consents.ageConfirm}
                  onCheckedChange={(checked) =>
                    setConsents((s) => ({ ...s, ageConfirm: checked }))
                  }
                  label="I confirm I am at least 18 years old and agree to participate"
                  highlight
                />
              </div>
            </div>

            {/* Anti-bot verification (Cloudflare Turnstile) — only rendered when enabled */}
            {botCheck !== "off" && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex flex-col items-center gap-2">
                  <div ref={widgetRef} />
                  {botCheck === "loading" && (
                    <p className="text-sm text-gray-400">Loading verification…</p>
                  )}
                  {botCheck === "failed" && (
                    <div className="text-center">
                      <p className="text-sm text-red-600 mb-2">
                        Verification failed. Please try again.
                      </p>
                      <button
                        onClick={retryBotCheck}
                        className="text-sm text-blue-600 underline"
                      >
                        Retry verification
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 text-center max-w-md">
                    This study uses Cloudflare Turnstile to verify you are a human
                    participant. Cloudflare processes your IP address and browser
                    characteristics for this purpose only.
                  </p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-8">
              <button
                onClick={handleConsent}
                disabled={!allConsented || !botCheckPassed}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
                  allConsented && botCheckPassed
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {allConsented && botCheckPassed ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircleIcon className="w-5 h-5" />
                    I Agree — Begin Study
                  </span>
                ) : !allConsented ? (
                  `Please confirm all ${totalConsents} items above`
                ) : (
                  "Please complete the verification above"
                )}
              </button>
            </div>

            {/* Ethics Footer */}
            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">
                This study has been approved by the Institute for Technology and Humanity
                Research Ethics Committee at the University of Cambridge
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Section Component
function Section({ title, icon, children }) {
  return (
    <div className="mt-6 first:mt-0">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-blue-600">{icon}</span>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// Quick Stat Component
function QuickStat({ icon, label, value }) {
  return (
    <div className="py-4 px-3 text-center">
      <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
        {icon}
      </div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
  );
}

// Expectation Item Component
function ExpectationItem({ number, title, description }) {
  return (
    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="w-7 h-7 flex-shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
        {number}
      </div>
      <div>
        <p className="font-medium text-gray-900 text-sm">{title}</p>
        <p className="text-sm text-gray-600 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

// Right Card Component
function RightCard({ icon, title, description }) {
  return (
    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-start gap-3">
      <div className="w-8 h-8 flex-shrink-0 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="font-medium text-gray-900 text-sm">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

// Consent Checkbox Component
function ConsentCheckbox({ checked, onCheckedChange, label, highlight = false }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
        checked
          ? highlight
            ? "bg-emerald-50 border border-emerald-200"
            : "bg-blue-50 border border-blue-200"
          : "bg-gray-50 border border-transparent hover:bg-gray-100"
      }`}
    >
      <div className="relative flex-shrink-0 mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className={`w-5 h-5 rounded flex items-center justify-center transition-all duration-200 ${
            checked
              ? highlight
                ? "bg-emerald-600"
                : "bg-blue-600"
              : "bg-white border-2 border-gray-300"
          }`}
        >
          {checked && (
            <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6L5 9L10 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>
      <span
        className={`text-sm transition-colors duration-200 ${
          checked ? "text-gray-900" : "text-gray-600"
        }`}
      >
        {label}
      </span>
    </label>
  );
}

// Icons
function ShieldCheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
